import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, Input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { FilterDateSelectValue } from '../../entities/filterDateSelect/filter-date-select-value';
import { User } from '../../entities/user/user';
import { ApolloService } from '../../services/apollo.service';
import * as fromGraphQl from '../notification-diagnostics/graphql';
import { DELIVERY_CHANNEL_VALUES, deliveryChannelLabelKey, FilterOption } from '../notification-diagnostics/notification-labels';

/**
 * Server-Default. Die Obergrenze von 100 wird serverseitig still geklemmt (MAX_LIMIT).
 *
 * Filterzustand und Cursor-Mechanik hier sind bewusst eine Kopie von
 * notification-delivery-list.component.ts (andere Filtertypen, andere Query-Dokumente) — eine
 * Korrektur an loadMore()/queryPage() hier zieht dort nicht automatisch mit, das muss von Hand
 * nachgezogen werden.
 */
const PAGE_SIZE = 50;

/**
 * Nachrichtenliste der Diagnose (#737) — die Eltern-Ebene.
 *
 * Eine Zeile je Notification, die Kinder als Kanalspalten. Der Alltagsfall des Supports:
 * gemeldet wird nie „meine Push-Zustellung ist gescheitert", sondern „ich habe nichts bekommen".
 *
 * Filter bewusst nur Empfaenger, Typ und Zeitraum — Kanal und Status gibt es nur im Zustell-Tab.
 */
@Component({
  selector: 'app-notification-admin-list',
  templateUrl: './notification-admin-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationAdminListComponent implements OnInit {

  private readonly apolloService = inject(ApolloService);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Dieselbe Quelle wie die Kanalspalten der Zeile (notification-admin-row.component.ts): sonst
   * laufen Kopfzeile und Spalten auseinander, sobald sich die Kanal-Reihenfolge einmal aendert.
   */
  readonly channels = DELIVERY_CHANNEL_VALUES;

  // --- Daten vom Container. Klassisches @Input mit Signal dahinter, damit der DOM-freie Test die
  // Komponente ueber TestBed.inject holen kann. Kein Alias: @angular-eslint/no-input-rename
  // verbietet @Input('users') set usersInput(...), deshalb traegt das Backing-Signal den
  // "Value"-Suffix statt des Inputs.
  readonly usersValue = signal<User[]>([]);

  @Input() set users(value: User[] | null) {
    this.usersValue.set(value ?? []);
  }

  readonly typeOptionsValue = signal<FilterOption[]>([]);

  @Input() set typeOptions(value: FilterOption[] | null) {
    this.typeOptionsValue.set(value ?? []);
  }

  // --- Filterzustand
  readonly recipientUserId = signal<string | null>(null);
  readonly typeKey = signal<string | null>(null);
  readonly dateRange = signal<FilterDateSelectValue | null>(null);

  // --- Ergebnis
  readonly items = signal<fromGraphQl.NotificationAdminEntry[]>([]);
  private readonly nextCursor = signal<string | null>(null);
  readonly loading = signal(false);
  readonly loadingMore = signal(false);
  readonly error = signal(false);
  readonly hasMore = computed(() => this.nextCursor() !== null);

  private readonly userNames = computed(() => {
    const map = new Map<string, string>();
    for (const user of this.usersValue()) {
      map.set(user.id, user.displayName);
    }
    return map;
  });

  ngOnInit(): void {
    this.loadFirstPage();
  }

  recipientName(userId: string): string | null {
    return this.userNames().get(userId) ?? null;
  }

  channelLabelKey(channel: string): string {
    return deliveryChannelLabelKey(channel);
  }

  onRecipientChange(userId: string | null): void {
    this.recipientUserId.set(userId ?? null);
    this.loadFirstPage();
  }

  onTypeKeyChange(typeKey: string | null): void {
    this.typeKey.set(typeKey ?? null);
    this.loadFirstPage();
  }

  onDateRangeChange(range: FilterDateSelectValue | null): void {
    this.dateRange.set(range ?? null);
    this.loadFirstPage();
  }

  /**
   * `filter` ist non-null: ohne gesetzten Filter geht ein leeres Objekt ueber die Leitung, niemals
   * null und niemals ein Objekt mit undefined-Werten.
   */
  buildFilter(): fromGraphQl.NotificationAdminFilter {
    const filter: fromGraphQl.NotificationAdminFilter = {};
    const recipientUserId = this.recipientUserId();
    const typeKey = this.typeKey();
    const range = this.dateRange();

    if (recipientUserId) {
      filter.recipientUserId = recipientUserId;
    }
    if (typeKey) {
      filter.typeKey = typeKey;
    }
    if (range?.fromDate) {
      filter.from = range.fromDate;
    }
    if (range?.toDate) {
      filter.to = range.toDate;
    }
    return filter;
  }

  loadFirstPage(): void {
    this.loading.set(true);
    this.error.set(false);

    this.queryPage(undefined).subscribe({
      next: (result) => {
        const page = result?.data?.getNotificationAdminPage;
        this.items.set(page?.items ?? []);
        this.nextCursor.set(page?.nextCursor ?? null);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.error.set(true);
      }
    });
  }

  /**
   * Das Ende der Liste sagt ausschliesslich nextCursor === null. Nie aus einer kuerzeren Antwort
   * als angefragt darauf schliessen — der Server klemmt limit still bei 100.
   */
  loadMore(): void {
    const cursor = this.nextCursor();
    if (!cursor || this.loadingMore()) {
      return;
    }
    this.loadingMore.set(true);

    this.queryPage(cursor).subscribe({
      next: (result) => {
        const page = result?.data?.getNotificationAdminPage;
        this.items.update((items) => [...items, ...(page?.items ?? [])]);
        this.nextCursor.set(page?.nextCursor ?? null);
        this.loadingMore.set(false);
      },
      error: () => {
        // Bestehende Zeilen bleiben stehen, der Knopf bleibt aktiv fuer einen erneuten Versuch.
        this.loadingMore.set(false);
      }
    });
  }

  /** network-only ist Pflicht: der Default von apollo.query ist cache-first. */
  private queryPage(cursor: string | undefined) {
    return this.apolloService
      .query<fromGraphQl.NotificationAdminPageResponse>(
        fromGraphQl.NOTIFICATION_ADMIN_PAGE_QUERY,
        { filter: this.buildFilter(), cursor, limit: PAGE_SIZE },
        { fetchPolicy: 'network-only' }
      )
      .pipe(take(1), takeUntilDestroyed(this.destroyRef));
  }
}
